/**
 * @author NikolaV
 */
import { computed, makeObservable, observable } from 'mobx';
import { Ajax } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { DeleteGeofenceDialog } from "@pages/trucks/ui/DeleteGeofenceDialog";
import { GeofenceType } from '@shared/trucks/GeofenceFeatureFactory';


export class GeofencesStore implements ICrudStore<IGeofence> {
    @observable items: IGeofence[] = [];
    @observable sortKey = 'title';
    @observable isDoingAsyncWork: boolean = false;
    @observable highlightIds = [];

    constructor() {
        makeObservable(this);
    }

    @computed
    get loadAreas(): IGeofence[] {
        return this.items.filter(g => (g.type == GeofenceType.LOAD || g.type == GeofenceType.HYBRID));
    }

    @computed
    get unloadAreas(): IGeofence[] {
        return this.items.filter(g => (g.type == GeofenceType.UNLOAD || g.type == GeofenceType.HYBRID));
    }

    @computed
    get sortedItems(): IGeofence[] {
        return this.items.slice().sort((a, b) => {
            if (this.sortKey === 'date') {
                [a, b] = [b, a];
            }
            return alphaNumSort(a[this.sortKey], b[this.sortKey]);
        });
    }

    fetch = () => {
        this.isDoingAsyncWork = true;
        return Ajax({
            url: 'project_masshaul_areas.json',
        }).done(res => this.items = res.areas)
          .always(() => this.isDoingAsyncWork = false);
    };

    create = (geofence: IGeofence) => {
        this.isDoingAsyncWork = true;
        return Ajax({
            url: 'add_project_masshaul_areas.json',
            method: 'post',
            data: {
                materialId: geofence.materialId,
                vehicleIds: geofence.attached_trucks,
                areaPoints: geofence.geometry,
                title: geofence.title,
                type: geofence.type,
                unit: geofence.unit,
                materialIds: geofence.materialIds,
            },
        }).always(() => this.isDoingAsyncWork = false);
    };

    update = (geofence: IGeofence) => {
        this.isDoingAsyncWork = true;
        return Ajax({
            url: 'edit_project_masshaul_area.json',
            method: 'post',
            data: {
                areaId: geofence.id,
                materialId: geofence.materialId,
                vehicleIds: geofence.attached_trucks,
                areaPoints: geofence.geometry,
                title: geofence.title,
                unit: geofence.unit,
                type: geofence.type,
                materialIds: geofence.materialIds,
            },
        }).always(() => this.isDoingAsyncWork = false);
    };

    remove = async (itemId: number) => {
        this.isDoingAsyncWork = true;

        if (!$.isNumeric(itemId)) return false;

        let usedInTrips = false;
        await this.checkIsAreaInTrips(itemId)
            .done(resp => usedInTrips = resp.isUsed)
            .fail(Notify.error)
            .always(() => this.isDoingAsyncWork = false);

        if (usedInTrips) {
            const geofenceType = this.items.find(m => m.id === itemId).type;
            const geofencesLengthByType = this.items.filter(m => (m.type === GeofenceType.HYBRID || m.type === geofenceType)).length;

            if (geofencesLengthByType === 1) {
                Notify.warn(__('trucks.geofence.delete_area_no_last'));
                return false;
            }
        }

        DeleteGeofenceDialog.open(this, itemId, usedInTrips, () => {
            this.items.splice(this.items.findIndex(m => m.id === itemId), 1);
        });
    };

    checkIfGeofenceTitleAlreadyExists(title: string, id?: number): boolean {
        for (const item of this.items) {
            if (item.id !== id && item.title === title) return true;
        }
        return false;
    }

    checkIsAreaInTrips = (areaId: number) => {
        return Ajax({ url: `/project_masshaul_area/${areaId}/trips` });
    };
}
